import { createContext, useEffect, useMemo, useState } from 'react'
import { THEME_STORAGE_KEY, ThemeModes, getStoredThemeMode, resolveTheme } from './theme'

const ThemeContext = createContext({
  mode: ThemeModes.system,
  resolvedTheme: ThemeModes.light,
  setMode: () => {},
})

function applyThemeToDocument(mode) {
  const resolved = resolveTheme(mode)
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved
    document.documentElement.dataset.themeMode = mode
  }
  return resolved
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => getStoredThemeMode())
  const [resolvedTheme, setResolvedTheme] = useState(() => applyThemeToDocument(getStoredThemeMode()))

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (mode !== ThemeModes.system) return
      setResolvedTheme(applyThemeToDocument(ThemeModes.system))
    }
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange)
      return () => media.removeEventListener('change', handleChange)
    }
    media.addListener(handleChange)
    return () => media.removeListener(handleChange)
  }, [mode])

  const setMode = (nextMode) => {
    const safeMode = Object.values(ThemeModes).includes(nextMode) ? nextMode : ThemeModes.system
    setModeState(safeMode)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, safeMode)
    }
    setResolvedTheme(applyThemeToDocument(safeMode))
  }

  useEffect(() => {
    setResolvedTheme(applyThemeToDocument(mode))
  }, [mode])

  const value = useMemo(() => ({ mode, resolvedTheme, setMode }), [mode, resolvedTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export default ThemeContext
